import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'

/**
 * Fetch the editable rows for a bulk-patch session rooted at the
 * chosen org.
 *
 * Server contract: `GET /aws/deals/bulk-patch/candidates?org_pk=ORG_<id>`
 *
 * Cached for 30s — fresh enough that data stays accurate during a
 * single edit session, infrequent enough not to thrash the BE on
 * page revisits.
 */

export type CandidateBlockReason =
  | 'deal_terminal'
  | 'no_active_deal'
  | 'no_products'

export type OrgRelationship = 'self' | 'child'

export interface CandidateRow {
  key: string
  org_pk: string
  org_name: string
  org_relationship: OrgRelationship
  deal_pk?: string
  deal_uuid?: string
  deal_name?: string
  deal_stage?: string
  deal_status?: string
  deal_dealStatus?: string
  semantic_version?: string
  /** True iff the deal has an explicit predecessorId — enables the
   *  upsell/downsell label vs. the prior contract. */
  has_predecessor?: boolean
  /** True iff the deal already exists in Heartbeat (contractId set or
   *  heartbeat_synced flag). When false, bulk-patch skips the push. */
  deal_in_heartbeat?: boolean
  product_pk?: string
  product_name?: string
  service_line?: string
  current_price?: number
  current_margin?: number
  current_start_date?: string
  current_end_date?: string
  editable: boolean
  block_reason?: CandidateBlockReason
  block_message?: string
}

export interface CandidatesResponse {
  root_org: {
    pk: string
    name: string
    has_children: boolean
  }
  total_orgs: number
  total_editable_rows: number
  total_blocked_rows: number
  rows: CandidateRow[]
}

export const useBulkPatchCandidates = (
  orgPK: string | null,
): UseQueryResult<CandidatesResponse, Error> => {
  return useQuery<CandidatesResponse, Error>({
    queryKey: ['bulk-patch-candidates', orgPK],
    enabled: Boolean(orgPK),
    staleTime: 30_000,
    queryFn: async (): Promise<CandidatesResponse> => {
      const res = await axios.get<CandidatesResponse>(
        `${baseUrl}/aws/deals/bulk-patch/candidates`,
        { params: { org_pk: orgPK } },
      )
      return res.data
    },
  })
}
